import { Box, Typography, Button, Tab } from '@mui/material';
import { useMyWorklist } from './hooks';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { WorklistItem } from 'components/WorklistItem';
import {
  AllTasks,
  InProgressTasks,
  NotStartedTasks,
  CompletedTasks,
} from './components';
import { UrgentTasks } from './components/UrgentTasks/UrgentTasks';
import { AddTask } from '@components';
import { theme } from '@store';
import { t } from 'i18next';
import { formatCount } from '@util/number';

export const MyWorklist = () => {
  const {
    tabIndex,
    handleChangeTab,
    handleNavigate,
    incompleteCount,
    completedTodayCount,
    completedThisWeekCount,
    completedThisMonthCount,
    selectedTaskId,
    totalCount,
    completedCount,
    inProgressCount,
    notStartedCount,
    urgentCount,
    myWorklistTabIndex,
    handleCloseDrawer,
  } = useMyWorklist();

  return (
    <>
      {/* Header section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h4' sx={{ mb: 2, mt: 2 }}>
          {t('worklist.myWorklist')}
        </Typography>
        <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Button
            sx={{
              fontWeight: 'regular',
              fontSize: '1rem',
              height: '100%',
            }}
            variant='outlined'
            onClick={handleNavigate}
          >
            {t('worklist.viewAllTask')}
          </Button>
          <AddTask />
        </Box>
      </Box>
      {/* Metrics section */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          gap: '1rem',
          pb: '2rem',
          [theme.breakpoints.down(1536)]: {
            display: 'none',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 16px',
            backgroundColor: theme.palette.common.white,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            width: '100%',
          }}
        >
          <Typography variant='subtitle2'>{t('worklist.overdue')}</Typography>
          <Typography variant='h4'>{formatCount(incompleteCount)}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 16px',
            backgroundColor: theme.palette.common.white,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            width: '100%',
          }}
        >
          <Typography variant='subtitle2'>
            {t('worklist.completedToday')}
          </Typography>
          <Typography variant='h4'>
            {formatCount(completedTodayCount)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 16px',
            backgroundColor: theme.palette.common.white,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            width: '100%',
          }}
        >
          <Typography variant='subtitle2'>
            {t('worklist.completedThisWeek')}
          </Typography>
          <Typography variant='h4'>
            {formatCount(completedThisWeekCount)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 16px',
            backgroundColor: theme.palette.common.white,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            width: '100%',
          }}
        >
          <Typography variant='subtitle2'>
            {t('worklist.completedThisMonth')}
          </Typography>
          <Typography variant='h4'>
            {formatCount(completedThisMonthCount)}
          </Typography>
        </Box>
      </Box>
      {/* Tabs section */}
      <TabContext value={tabIndex}>
        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 2,
              width: '100%',
            }}
          >
            <TabList onChange={handleChangeTab}>
              <Tab
                label={t('worklist.allTasks', {
                  number: totalCount > 0 ? ` (${totalCount})` : '',
                })}
                value={myWorklistTabIndex.all}
              />
              <Tab
                label={t('worklist.urgentTasks', {
                  number: urgentCount > 0 ? ` (${urgentCount})` : '',
                })}
                value={myWorklistTabIndex.urgent}
              />
              <Tab
                label={t('worklist.inProgressTasks', {
                  number: inProgressCount > 0 ? ` (${inProgressCount})` : '',
                })}
                value={myWorklistTabIndex.inProgress}
              />
              <Tab
                label={t('worklist.notStarted', {
                  number: notStartedCount > 0 ? ` (${notStartedCount})` : '',
                })}
                value={myWorklistTabIndex.notStarted}
              />
              <Tab
                label={t('worklist.completedTasks', {
                  number: completedCount > 0 ? ` (${completedCount})` : '',
                })}
                value={myWorklistTabIndex.completed}
              />
            </TabList>
          </Box>
          <>
            <TabPanel
              sx={{
                padding: '0 !important',
                width: '100%',
              }}
              value={myWorklistTabIndex.all}
            >
              <AllTasks />
            </TabPanel>
            <TabPanel
              sx={{
                padding: '0 !important',
              }}
              value={myWorklistTabIndex.urgent}
            >
              <UrgentTasks />
            </TabPanel>
            <TabPanel
              sx={{
                padding: '0 !important',
              }}
              value={myWorklistTabIndex.inProgress}
            >
              <InProgressTasks />
            </TabPanel>
            <TabPanel
              sx={{
                padding: '0 !important',
              }}
              value={myWorklistTabIndex.notStarted}
            >
              <NotStartedTasks />
            </TabPanel>
            <TabPanel
              sx={{
                padding: '0 !important',
              }}
              value={myWorklistTabIndex.completed}
            >
              <CompletedTasks />
            </TabPanel>
          </>
        </Box>
      </TabContext>
      {selectedTaskId && (
        <WorklistItem
          open={!!selectedTaskId}
          itemId={selectedTaskId}
          handleClose={handleCloseDrawer}
        />
      )}
    </>
  );
};
